<script setup lang="ts">
import { computed, onMounted, reactive, ref } from 'vue'
import { RouterLink, useRoute } from 'vue-router'
import { useI18n } from 'vue-i18n'
import { formatApiError, useApiClient } from '@yuksalish/api-client'
import { formatDisplayDateTime } from '@yuksalish/ui'
import { closeLotStage, getProductionLot, getProductionOrder, listTechnicalPassports, postLotStageProgress } from '../productService'
import type { ProductionEmployee, ProductionLot, TechnicalPassportQcItem } from '../types'
import EmployeePickerModal from '../components/EmployeePickerModal.vue'

const route = useRoute(), client = useApiClient()
const { t } = useI18n()
const orderId = computed(() => Number(route.params.id)), lotNumber = computed(() => String(route.params.lotNumber))
const lot = ref<ProductionLot | null>(null), loading = ref(false), saving = ref(false), error = ref(''), formError = ref('')
const employeePickerOpen = ref(false), selectedEmployee = ref<ProductionEmployee | null>(null)
const qcItems = ref<TechnicalPassportQcItem[]>([]), checkedQcItems = ref(new Set<number>()), qcNotice = ref('')
const form = reactive({ completed_delta: 0, rejected_delta: 0, rework_delta: 0, employee_id: undefined as number | undefined, notes: '' })
const rejectedTotal = computed(() => lot.value?.entries?.reduce((total, entry) => total + Number(entry.rejected_delta || 0), 0) ?? 0)
const reworkTotal = computed(() => lot.value?.entries?.reduce((total, entry) => total + Number(entry.rework_delta || 0), 0) ?? 0)
const exceptionTotal = computed(() => rejectedTotal.value + reworkTotal.value)
const requiredQcItems = computed(() => qcItems.value.map((item, index) => ({ item, index })).filter(({ item }) => Boolean(item.is_required)))
const qcReady = computed(() => requiredQcItems.value.every(({ index }) => checkedQcItems.value.has(index)))

function printIdentity() { window.print() }
async function load() {
  loading.value = true
  try {
    lot.value = await getProductionLot(client, orderId.value, lotNumber.value)
    checkedQcItems.value = new Set()
    qcItems.value = []
    qcNotice.value = ''
    try {
      const order = await getProductionOrder(client, orderId.value)
      const passports = await listTechnicalPassports(client, order.product_id)
      const passport = passports.find((item) => item.status === 'approved')
      qcItems.value = (passport?.qc_items ?? []).filter((item) => normalizeStage(item.stage) === normalizeStage(lot.value?.current_stage_code))
      if (!passport) qcNotice.value = t('engineering.productionOrder.qcNoApprovedPassport')
      else if (!qcItems.value.length) qcNotice.value = t('engineering.productionOrder.qcNoStageChecks')
    } catch {
      qcNotice.value = t('engineering.productionOrder.qcLoadFailed')
    }
  }
  catch (cause) { error.value = formatApiError(cause) }
  finally { loading.value = false }
}
function normalizeStage(value?: string) { return (value ?? '').trim().toLowerCase().replace(/[\s_-]+/g, '') }
function toggleQcItem(index: number) {
  const next = new Set(checkedQcItems.value)
  if (next.has(index)) next.delete(index)
  else next.add(index)
  checkedQcItems.value = next
}
function selectEmployee(employee: ProductionEmployee) {
  selectedEmployee.value = employee
  form.employee_id = employee.id
  employeePickerOpen.value = false
}
async function executeProgress() {
  if (!lot.value || !form.employee_id) return
  saving.value = true
  try {
    await postLotStageProgress(client, orderId.value, lot.value.lot_number, lot.value.current_stage_code, {
      completed_delta: Number(form.completed_delta), rejected_delta: Number(form.rejected_delta),
      rework_delta: Number(form.rework_delta), employee_id: Number(form.employee_id), notes: form.notes || undefined,
    })
    form.completed_delta = form.rejected_delta = form.rework_delta = 0
    form.notes = ''
    await load()
  } catch (cause) { formError.value = formatApiError(cause) }
  finally { saving.value = false }
}
function progress() {
  formError.value = ''
  if (!form.employee_id || form.completed_delta + form.rejected_delta + form.rework_delta <= 0) {
    formError.value = t('engineering.productionOrder.lotProgressValidation')
    return
  }
  if (window.confirm(t('engineering.productionOrder.confirmProgress', { completed: form.completed_delta, rejected: form.rejected_delta, rework: form.rework_delta, lot: lot.value?.lot_number }))) void executeProgress()
}
async function executeClose() {
  if (!lot.value || !form.employee_id) return
  saving.value = true
  try {
    await closeLotStage(client, orderId.value, lot.value.lot_number, lot.value.current_stage_code, {
      employee_id: Number(form.employee_id), notes: form.notes || undefined,
    })
    await load()
  } catch (cause) { formError.value = formatApiError(cause) }
  finally { saving.value = false }
}
function closeStage() {
  formError.value = ''
  if (!form.employee_id) { formError.value = t('engineering.productionOrder.lotEmployeeRequired'); return }
  if (!qcReady.value) { formError.value = t('engineering.productionOrder.lotQcRequired'); return }
  if (window.confirm(t('engineering.productionOrder.confirmClose', { stage: lot.value?.current_stage_code, lot: lot.value?.lot_number }))) void executeClose()
}
onMounted(load)
</script>

<template>
  <div class="page-toolbar">
    <RouterLink class="back" :data-tooltip="t('engineering.productionOrder.backToLotQueue')" :to="`/production-orders/${orderId}/lots`">{{ t('engineering.productionOrder.backToLotQueue') }}</RouterLink>
    <div class="page-toolbar__actions">
      <button v-if="lot" class="print-button" type="button" @click="printIdentity">{{ t('engineering.productionOrder.printLotCard') }}</button>
    </div>
  </div>
  <div v-if="loading" class="state">{{ t('engineering.productionOrder.loadingLot') }}</div>
  <div v-else-if="error" class="state error">{{ error }}</div>
  <template v-else-if="lot">
    <header>
      <div><p class="eyebrow">{{ t('engineering.productionOrder.lotEyebrow') }}</p><h2>{{ lot.lot_number }}</h2><p>{{ t('engineering.productionOrder.currentStageLabel') }} <strong>{{ lot.current_stage_code }}</strong></p></div>
      <div class="lot-actions"><span class="badge">{{ t(`engineering.state.${lot.status}`) }}</span></div>
    </header>
    <section class="summary">
      <div><span>{{ t('engineering.productionOrder.initial') }}</span><strong>{{ lot.initial_quantity }}</strong></div>
      <div><span>{{ t('engineering.productionOrder.current') }}</span><strong>{{ lot.current_quantity }}</strong></div>
      <div :class="{ exception: rejectedTotal > 0 }"><span>{{ t('engineering.productionOrder.rejected') }}</span><strong>{{ rejectedTotal }}</strong></div>
      <div :class="{ warning: reworkTotal > 0 }"><span>{{ t('engineering.productionOrder.rework') }}</span><strong>{{ reworkTotal }}</strong></div>
    </section>
    <section v-if="exceptionTotal" class="exception-banner" role="alert">
      <strong>{{ t('engineering.productionOrder.exceptionReviewRequired') }}</strong>
      <span>{{ t('engineering.productionOrder.exceptionReviewSummary', { rejected: rejectedTotal, rework: reworkTotal }) }}</span>
    </section>
    <section class="qc-handoff">
      <div class="section-heading">
        <div><p class="eyebrow">{{ t('engineering.productionOrder.qcHandover') }}</p><h3>{{ t('engineering.productionOrder.stageChecks', { stage: lot.current_stage_code }) }}</h3></div>
        <span class="qc-state" :class="{ ready: qcReady }">{{ qcReady ? t('engineering.productionOrder.readyToClose') : t('engineering.productionOrder.checksRequired') }}</span>
      </div>
      <p v-if="qcNotice" class="muted">{{ qcNotice }}</p>
      <label v-for="(item, index) in qcItems" :key="item.id ?? index" class="qc-item">
        <input type="checkbox" :checked="checkedQcItems.has(index)" @change="toggleQcItem(index)">
        <span><strong>{{ item.item_name || t('engineering.productionOrder.qualityCheck') }}</strong><small>{{ item.acceptable_value || t('engineering.productionOrder.confirmAcceptance') }}<template v-if="item.inspection_method"> · {{ item.inspection_method }}</template></small></span>
        <em v-if="item.is_required">{{ t('engineering.productionOrder.qcRequired') }}</em>
      </label>
    </section>
    <form @submit.prevent="progress">
      <h3>{{ t('engineering.productionOrder.recordProgress') }}</h3>
      <div class="grid">
        <label>{{ t('engineering.productionOrder.completed') }}<input v-model.number="form.completed_delta" inputmode="numeric" min="0" type="number"></label>
        <label>{{ t('engineering.productionOrder.rejected') }}<input v-model.number="form.rejected_delta" inputmode="numeric" min="0" type="number"></label>
        <label>{{ t('engineering.productionOrder.rework') }}<input v-model.number="form.rework_delta" inputmode="numeric" min="0" type="number"></label>
        <label>{{ t('engineering.productionOrder.employee') }} *<button class="employee-select" type="button" @click="employeePickerOpen = true">{{ selectedEmployee ? `${selectedEmployee.first_name} ${selectedEmployee.last_name} · ${selectedEmployee.employee_code}` : t('engineering.productionOrder.selectProductionEmployee') }}</button></label>
        <label class="wide">{{ t('engineering.productionOrder.notes') }}<textarea v-model="form.notes" rows="2"></textarea></label>
      </div>
      <p v-if="formError" class="error">{{ formError }}</p>
      <footer>
        <button :disabled="saving" type="submit">{{ saving ? t('engineering.productionOrder.saving') : t('engineering.productionOrder.saveProgress') }}</button>
        <button class="close" :disabled="saving || !form.employee_id || !qcReady" type="button" @click="closeStage">{{ t('engineering.productionOrder.closeCurrentStage') }}</button>
      </footer>
    </form>
    <section>
      <h3>{{ t('engineering.productionOrder.history') }}</h3><p v-if="!lot.entries?.length">{{ t('engineering.productionOrder.noActivity') }}</p>
      <article v-for="entry in lot.entries" :key="entry.id"><strong>{{ entry.action }}</strong><span>{{ t('engineering.productionOrder.historyDeltas', { completed: entry.completed_delta, rejected: entry.rejected_delta, rework: entry.rework_delta }) }}</span><small>{{ entry.employee_name || t('engineering.productionOrder.employeeFallback') }}, {{ formatDisplayDateTime(entry.created_at) || '—' }}</small></article>
    </section>
    <EmployeePickerModal :open="employeePickerOpen" @close="employeePickerOpen = false" @select="selectEmployee" />
  </template>
</template>

<style scoped>
.back{display:inline-block;margin-bottom:20px;color:#285b9a;font-weight:700}header{display:flex;justify-content:space-between}.lot-actions{display:flex;align-items:center;gap:10px}.eyebrow{margin:0;color:#66726e;font-size:12px;font-weight:800}h2{margin:4px 0}.badge{padding:7px 10px;color:#236451;background:#eaf7f2}.summary{display:grid;grid-template-columns:repeat(4,1fr);gap:10px;margin:16px 0}.summary div,form,section{padding:18px;border:1px solid #d7dfdc;background:#fff}.summary div{display:grid}.summary .exception{border-color:#efb5ad;background:#fff3f1}.summary .warning{border-color:#ecd28a;background:#fffae9}.exception-banner{display:flex;justify-content:space-between;gap:12px;margin-bottom:16px;border-color:#efb5ad;color:#852d22;background:#fff3f1}.section-heading{display:flex;align-items:center;justify-content:space-between;gap:12px}.section-heading h3{margin:3px 0}.qc-handoff{margin-bottom:16px}.qc-state{padding:6px 9px;color:#852d22;background:#fff3f1;font-size:12px;font-weight:800}.qc-state.ready{color:#236451;background:#eaf7f2}.qc-item{grid-template-columns:auto 1fr auto;align-items:start;margin-top:8px;padding:11px;border:1px solid #e1e7e5}.qc-item input{margin-top:3px}.qc-item span{display:grid;gap:3px}.qc-item small,.muted{color:#66726e}.qc-item em{color:#852d22;font-size:11px;text-transform:uppercase}.grid{display:grid;grid-template-columns:repeat(2,1fr);gap:12px}label{display:grid;gap:5px;font-weight:700}.wide{grid-column:1/-1}input,textarea{padding:9px;border:1px solid #b9c5c1}footer{display:flex;gap:8px;margin-top:14px}button{padding:10px 14px;border:0;color:#fff;background:#07996f;font-weight:700}.close,.print-button{background:#3568a8}.error{color:#852d22}.state{display:grid;min-height:220px;place-items:center}article{display:grid;gap:3px;padding:10px;border-bottom:1px solid #e1e7e5}article small{color:#66726e}@media(max-width:700px){.summary{grid-template-columns:repeat(2,1fr)}.exception-banner,.section-heading{align-items:stretch;flex-direction:column}.grid{grid-template-columns:1fr}.wide{grid-column:auto}}@media print{.back,.print-button,form,section:not(.summary){display:none!important}header{margin-bottom:18px}.summary{display:grid!important}}
</style>
